import {
  BULK_MAX_ADS,
  CAMPAIGN_OBJECTIVES,
  DEFAULT_AD_NAME_PATTERN,
  DEFAULT_ADSET_NAME_PATTERN,
  destinationOption,
  DESTINATION_TYPE_LABELS,
  defaultBillingEvent,
  OBJECTIVE_DESTINATIONS,
  objectiveLabel,
  optimizationGoalLabel,
  roleHas,
  WIZARD_STEP_LABELS,
  type BulkMatrixInput,
  type BulkPlanDto,
  type CreativeAssetDto,
  type DraftDto,
  type TargetingDraft,
  type TemplateDto,
} from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FileText, LayoutTemplate, Plus, Rocket, Trash2, Wand2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AccountPicker, readLastAccount } from "@/components/campaigns/account-picker";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AssetPicker, AssetThumb } from "@/components/creation/asset-picker";
import { PublicationBadge, PublicationProgress } from "@/components/creation/publication-progress";
import { TargetingEditor } from "@/components/creation/targeting-editor";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { errorMessage } from "@/lib/api";
import { creationApi, creationKey } from "@/lib/creation-api";
import { queries } from "@/lib/queries";
import { formatMoney, formatRelative } from "@/lib/utils";

interface AssistenteSearch {
  aba?: "rascunhos" | "massa" | "templates" | "publicacoes";
  conta?: string;
}

export const Route = createFileRoute("/app/$workspaceId/assistente")({
  validateSearch: (raw: Record<string, unknown>): AssistenteSearch => {
    const out: AssistenteSearch = {};
    if (
      raw.aba === "rascunhos" ||
      raw.aba === "massa" ||
      raw.aba === "templates" ||
      raw.aba === "publicacoes"
    )
      out.aba = raw.aba;
    if (typeof raw.conta === "string" && /^act_\d+$/.test(raw.conta)) out.conta = raw.conta;
    return out;
  },
  component: AssistentePage,
});

const STATUS: Record<
  DraftDto["status"],
  { label: string; variant: "secondary" | "warning" | "success" | "destructive" }
> = {
  rascunho: { label: "Rascunho", variant: "secondary" },
  publicando: { label: "Publicando…", variant: "warning" },
  publicado: { label: "Publicado", variant: "success" },
  erro: { label: "Falhou", variant: "destructive" },
};

function AssistentePage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const workspace = useQuery(queries.workspace(workspaceId));
  const canWrite = workspace.data ? roleHas(workspace.data.role, "campaigns:write") : false;
  const accounts = useQuery(queries.campaignAccounts(workspaceId));
  const account = search.conta ?? readLastAccount(workspaceId) ?? accounts.data?.[0]?.metaId ?? "";
  const aba = search.aba ?? "rascunhos";
  const setAba = (a: AssistenteSearch["aba"]) =>
    void navigate({
      search: (prev) => {
        const next: AssistenteSearch = {};
        if (prev.conta) next.conta = prev.conta;
        if (a) next.aba = a;
        return next;
      },
    });
  const drafts = useQuery(queries.drafts(workspaceId, account || undefined));
  const [toDelete, setToDelete] = useState<DraftDto | null>(null);

  const create = useMutation({
    mutationFn: () =>
      creationApi.createDraft(workspaceId, {
        adAccountMetaId: account,
        step: "campanha",
        data: {},
      }),
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      void navigate({
        to: "/app/$workspaceId/assistente/$draftId",
        params: { workspaceId, draftId: d.id },
      });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => creationApi.deleteDraft(workspaceId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      setToDelete(null);
      toast.success("Rascunho excluído.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <>
      <PageHeader
        title="Assistente de criação"
        description="Campanha → Conjunto → Anúncio, com rascunho salvo a cada etapa, prévia real e publicação com rollback."
        actions={
          canWrite && account ? (
            <Button onClick={() => create.mutate()} loading={create.isPending}>
              <Plus />
              Nova campanha
            </Button>
          ) : null
        }
      />
      {accounts.isPending ? (
        <LoadingState rows={3} />
      ) : accounts.isError ? (
        <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : accounts.data.length === 0 ? (
        <EmptyState
          icon={<Wand2 className="size-8" aria-hidden />}
          title="Nenhuma conta de anúncio"
          description="Conecte uma conta Meta para criar campanhas."
        />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <AccountPicker
              accounts={accounts.data}
              value={account}
              onChange={(v) => void navigate({ search: (prev) => ({ ...prev, conta: v }) })}
            />
            <Tabs value={aba} onValueChange={(v) => setAba(v as AssistenteSearch["aba"])}>
              <TabsList>
                <TabsTrigger value="rascunhos">Rascunhos</TabsTrigger>
                <TabsTrigger value="massa">Criação em massa</TabsTrigger>
                <TabsTrigger value="templates">Templates</TabsTrigger>
                <TabsTrigger value="publicacoes">Publicações</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {aba === "rascunhos" ? (
            <Card>
              <CardContent>
                {drafts.isPending ? (
                  <LoadingState rows={3} />
                ) : drafts.isError ? (
                  <ErrorState error={drafts.error} onRetry={() => void drafts.refetch()} />
                ) : drafts.data.length === 0 ? (
                  <EmptyState
                    icon={<FileText className="size-8" aria-hidden />}
                    title="Nenhum rascunho nesta conta"
                    description="Comece uma campanha nova ou use um template."
                    action={
                      canWrite ? (
                        <Button onClick={() => create.mutate()}>Nova campanha</Button>
                      ) : undefined
                    }
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rascunho</TableHead>
                        <TableHead>Etapa</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Pendências</TableHead>
                        <TableHead>Atualizado</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drafts.data.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell>
                            <Link
                              to="/app/$workspaceId/assistente/$draftId"
                              params={{ workspaceId, draftId: d.id }}
                              className="font-medium hover:underline"
                            >
                              {d.name || "Sem nome"}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {objectiveLabel(d.data.campaign.objective)} · {d.data.adsets.length}{" "}
                              conjunto(s) · {d.data.adsets.reduce((n, a) => n + a.ads.length, 0)}{" "}
                              anúncio(s) ·{" "}
                              {d.kind === "massa"
                                ? "em massa"
                                : d.kind === "duplicacao"
                                  ? "duplicação"
                                  : "assistente"}
                            </p>
                          </TableCell>
                          <TableCell>{WIZARD_STEP_LABELS[d.step]}</TableCell>
                          <TableCell>
                            <Badge variant={STATUS[d.status].variant}>
                              {STATUS[d.status].label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {d.issues.length === 0 ? (
                              <span className="text-emerald-700 dark:text-emerald-400">pronto</span>
                            ) : (
                              `${d.issues.length}`
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {formatRelative(d.updatedAt)}
                            {d.updatedByName ? ` · ${d.updatedByName}` : ""}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button asChild size="sm" variant="outline">
                                <Link
                                  to="/app/$workspaceId/assistente/$draftId"
                                  params={{ workspaceId, draftId: d.id }}
                                >
                                  Retomar
                                </Link>
                              </Button>
                              {canWrite ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive"
                                  aria-label="Excluir rascunho"
                                  onClick={() => setToDelete(d)}
                                >
                                  <Trash2 />
                                </Button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : null}

          {aba === "massa" ? (
            <BulkCreation
              workspaceId={workspaceId}
              account={account}
              canWrite={canWrite}
              currency={accounts.data.find((a) => a.metaId === account)?.currency ?? "BRL"}
            />
          ) : null}
          {aba === "templates" ? (
            <Templates workspaceId={workspaceId} account={account} canWrite={canWrite} />
          ) : null}
          {aba === "publicacoes" ? (
            <Publications workspaceId={workspaceId} account={account} />
          ) : null}
        </div>
      )}
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title={`Excluir o rascunho "${toDelete?.name ?? ""}"?`}
        description="Só o rascunho no IAGENIX é apagado; nada muda na Meta."
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => (toDelete ? remove.mutate(toDelete.id) : undefined)}
      />
    </>
  );
}

// ---- Criação em massa --------------------------------------------------------------------------------

const emptyTargeting = (): TargetingDraft => ({
  countries: ["BR"],
  regions: [],
  cities: [],
  ageMin: 18,
  ageMax: 65,
  genders: [],
  interests: [],
  customAudiences: [],
  excludedCustomAudiences: [],
  advantageAudience: false,
});

function BulkCreation({
  workspaceId,
  account,
  canWrite,
  currency,
}: {
  workspaceId: string;
  account: string;
  canWrite: boolean;
  currency: string;
}) {
  const options = useQuery(queries.creationOptions(workspaceId, account));
  const queryClient = useQueryClient();
  const [campaignName, setCampaignName] = useState("");
  const [objective, setObjective] =
    useState<BulkMatrixInput["campaign"]["objective"]>("OUTCOME_TRAFFIC");
  const [budget, setBudget] = useState("");
  const [destination, setDestination] = useState<string>("WEBSITE");
  const [goal, setGoal] = useState<string>("LINK_CLICKS");
  const [pixelId, setPixelId] = useState("");
  const [pageId, setPageId] = useState("");
  const [link, setLink] = useState("");
  const [audiences, setAudiences] = useState<{ label: string; targeting: TargetingDraft }[]>([
    { label: "Público 1", targeting: emptyTargeting() },
  ]);
  const [editingAudience, setEditingAudience] = useState<number | null>(null);
  const [creatives, setCreatives] = useState<CreativeAssetDto[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [texts, setTexts] = useState<{ label: string; primaryText: string; headline: string }[]>([
    { label: "Texto 1", primaryText: "", headline: "" },
  ]);
  const [adsetPattern, setAdsetPattern] = useState(DEFAULT_ADSET_NAME_PATTERN);
  const [adPattern, setAdPattern] = useState(DEFAULT_AD_NAME_PATTERN);
  const [plan, setPlan] = useState<BulkPlanDto | null>(null);
  const [activate, setActivate] = useState(false);
  const [publicationId, setPublicationId] = useState<string | null>(null);
  const destOption = destinationOption(objective, destination);

  const matrix = (): BulkMatrixInput => ({
    adAccountMetaId: account,
    campaign: {
      name: campaignName,
      objective,
      specialAdCategories: [],
      budgetMode: "CBO",
      budgetKind: "diario",
      budgetCents: Math.round(Number(budget.replace(",", ".")) * 100) || 0,
      bidStrategy: "LOWEST_COST_WITHOUT_CAP",
      abTest: false,
    },
    adsetBase: {
      destinationType: destination as BulkMatrixInput["adsetBase"]["destinationType"],
      optimizationGoal: goal as BulkMatrixInput["adsetBase"]["optimizationGoal"],
      billingEvent: defaultBillingEvent(goal as BulkMatrixInput["adsetBase"]["optimizationGoal"]),
      pixelId: pixelId || null,
      customEventType: pixelId ? "PURCHASE" : null,
      pageId: pageId || null,
      applicationId: null,
      objectStoreUrl: null,
      budgetKind: "diario",
      budgetCents: 0,
      bidAmountCents: null,
      startTime: null,
      endTime: null,
      placements: {
        automatic: true,
        publisherPlatforms: [],
        facebookPositions: [],
        instagramPositions: [],
        messengerPositions: [],
        audienceNetworkPositions: [],
      },
    },
    adBase: {
      pageId: pageId || null,
      instagramUserId: null,
      cta: "LEARN_MORE",
      link,
      utmEnabled: true,
      utm: {
        source: "facebook",
        medium: "paid",
        campaign: "{{campaign.name}}",
        content: "{{ad.name}}",
        term: "{{adset.name}}",
      },
      thumbnailAssetId: null,
    },
    audiences,
    creatives: creatives.map((c) => ({ label: c.name, format: c.kind, assetId: c.id })),
    texts: texts.map((t) => ({ ...t, description: "" })),
    adsetNamePattern: adsetPattern,
    adNamePattern: adPattern,
  });

  const preview = useMutation({
    mutationFn: () => creationApi.bulkPlan(workspaceId, matrix()),
    onSuccess: setPlan,
    onError: (err) => toast.error(errorMessage(err)),
  });
  const publish = useMutation({
    mutationFn: () => creationApi.bulkPublish(workspaceId, matrix(), activate),
    onSuccess: (p) => {
      setPublicationId(p.id);
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      void queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "campaigns"] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const total = audiences.length * creatives.length * texts.length;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Matriz: públicos × criativos × textos</CardTitle>
          <CardDescription>
            Cada público vira um conjunto; cada criativo × texto vira um anúncio dentro dele. Limite
            de segurança: {BULK_MAX_ADS} anúncios por publicação.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="m-nome">Nome da campanha</Label>
            <Input
              id="m-nome"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-obj">Objetivo</Label>
            <Select
              value={objective}
              onValueChange={(v) => {
                const o = v as typeof objective;
                setObjective(o);
                const first = OBJECTIVE_DESTINATIONS[o][0];
                setDestination(first?.destination ?? "WEBSITE");
                setGoal(first?.goals[0] ?? "LINK_CLICKS");
              }}
            >
              <SelectTrigger id="m-obj">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_OBJECTIVES.map((o) => (
                  <SelectItem key={o} value={o}>
                    {objectiveLabel(o)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-orc">Orçamento diário da campanha ({currency}, CBO)</Label>
            <Input
              id="m-orc"
              inputMode="decimal"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder="ex.: 100,00"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-dest">Local de conversão</Label>
            <Select
              value={destination}
              onValueChange={(v) => {
                setDestination(v);
                setGoal(destinationOption(objective, v)?.goals[0] ?? goal);
              }}
            >
              <SelectTrigger id="m-dest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OBJECTIVE_DESTINATIONS[objective].map((d) => (
                  <SelectItem key={d.destination} value={d.destination}>
                    {DESTINATION_TYPE_LABELS[d.destination]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-goal">Otimização</Label>
            <Select value={goal} onValueChange={setGoal}>
              <SelectTrigger id="m-goal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(destOption?.goals ?? []).map((g) => (
                  <SelectItem key={g} value={g}>
                    {optimizationGoalLabel(g)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {destOption?.needsPixel ? (
            <div className="space-y-1.5">
              <Label htmlFor="m-pixel">Pixel (evento Compra)</Label>
              <Select value={pixelId} onValueChange={setPixelId}>
                <SelectTrigger id="m-pixel">
                  <SelectValue placeholder="Escolha" />
                </SelectTrigger>
                <SelectContent>
                  {(options.data?.pixels ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="m-page">Página</Label>
            <Select value={pageId} onValueChange={setPageId}>
              <SelectTrigger id="m-page">
                <SelectValue placeholder="Escolha" />
              </SelectTrigger>
              <SelectContent>
                {(options.data?.pages ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-link">URL de destino</Label>
            <Input
              id="m-link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-pat1">Padrão de nome dos conjuntos</Label>
            <Input
              id="m-pat1"
              value={adsetPattern}
              onChange={(e) => setAdsetPattern(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="m-pat2">Padrão de nome dos anúncios</Label>
            <Input id="m-pat2" value={adPattern} onChange={(e) => setAdPattern(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Tokens: {"{campanha} {publico} {criativo} {texto} {n}"}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Públicos ({audiences.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {audiences.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  aria-label={`Nome do público ${i + 1}`}
                  value={a.label}
                  onChange={(e) =>
                    setAudiences(
                      audiences.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)),
                    )
                  }
                  className="h-8"
                />
                <Button size="sm" variant="outline" onClick={() => setEditingAudience(i)}>
                  Segmentar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="Remover público"
                  onClick={() => setAudiences(audiences.filter((_, k) => k !== i))}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setAudiences([
                    ...audiences,
                    { label: `Público ${audiences.length + 1}`, targeting: emptyTargeting() },
                  ])
                }
              >
                <Plus />
                Público
              </Button>
              {(options.data?.audiences ?? []).length > 0 ? (
                <select
                  className="h-8 rounded-md border bg-transparent px-2 text-xs"
                  value=""
                  aria-label="Adicionar público personalizado"
                  onChange={(e) => {
                    const a = options.data?.audiences.find((x) => x.id === e.target.value);
                    if (a)
                      setAudiences([
                        ...audiences,
                        {
                          label: a.name,
                          targeting: {
                            ...emptyTargeting(),
                            customAudiences: [{ id: a.id, name: a.name }],
                          },
                        },
                      ]);
                  }}
                >
                  <option value="">+ personalizado da conta…</option>
                  {options.data?.audiences.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Criativos ({creatives.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <ul className="space-y-1">
              {creatives.map((c) => (
                <li key={c.id} className="flex items-center gap-2 text-sm">
                  <AssetThumb asset={c} className="size-8 rounded" />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Remover criativo"
                    onClick={() => setCreatives(creatives.filter((x) => x.id !== c.id))}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
            <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
              <Plus />
              Da biblioteca
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Textos ({texts.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {texts.map((t, i) => (
              <div key={i} className="space-y-1 rounded-md border p-2">
                <div className="flex gap-2">
                  <Input
                    aria-label={`Rótulo do texto ${i + 1}`}
                    value={t.label}
                    onChange={(e) =>
                      setTexts(texts.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))
                    }
                    className="h-8"
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Remover texto"
                    onClick={() => setTexts(texts.filter((_, k) => k !== i))}
                  >
                    <Trash2 />
                  </Button>
                </div>
                <Textarea
                  aria-label={`Texto principal ${i + 1}`}
                  value={t.primaryText}
                  onChange={(e) =>
                    setTexts(
                      texts.map((x, k) => (k === i ? { ...x, primaryText: e.target.value } : x)),
                    )
                  }
                  placeholder="Texto principal"
                  className="min-h-16"
                />
                <Input
                  aria-label={`Título ${i + 1}`}
                  value={t.headline}
                  onChange={(e) =>
                    setTexts(
                      texts.map((x, k) => (k === i ? { ...x, headline: e.target.value } : x)),
                    )
                  }
                  placeholder="Título"
                  className="h-8"
                />
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setTexts([
                  ...texts,
                  { label: `Texto ${texts.length + 1}`, primaryText: "", headline: "" },
                ])
              }
            >
              <Plus />
              Texto
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3">
          <p className="text-sm">
            A matriz gera <strong>{audiences.length}</strong> conjunto(s) e <strong>{total}</strong>{" "}
            anúncio(s)
            {total > BULK_MAX_ADS ? (
              <span className="text-destructive"> — acima do limite de {BULK_MAX_ADS}.</span>
            ) : (
              "."
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => preview.mutate()} loading={preview.isPending}>
              Ver prévia da matriz
            </Button>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={activate}
                onChange={(e) => setActivate(e.target.checked)}
              />
              Ativar ao final
            </label>
            {canWrite ? (
              <Button
                onClick={() => publish.mutate()}
                loading={publish.isPending}
                disabled={
                  total === 0 ||
                  total > BULK_MAX_ADS ||
                  (plan?.issues.length ?? 0) > 0 ||
                  plan === null
                }
              >
                <Rocket />
                Publicar {total} anúncio(s)
              </Button>
            ) : null}
          </div>
          {plan ? (
            <div className="space-y-2">
              {plan.issues.length > 0 ? (
                <ul className="list-disc pl-4 text-sm text-destructive">
                  {plan.issues.map((i) => (
                    <li key={i.path}>{i.message}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-emerald-700 dark:text-emerald-400">
                  Sem pendências: {plan.adsetCount} conjunto(s) e {plan.adCount} anúncio(s) serão
                  criados (pausados).
                </p>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Conjunto</TableHead>
                    <TableHead>Anúncio</TableHead>
                    <TableHead>Público</TableHead>
                    <TableHead>Criativo</TableHead>
                    <TableHead>Texto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plan.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell>{r.adsetName}</TableCell>
                      <TableCell>{r.adName}</TableCell>
                      <TableCell>{r.audience}</TableCell>
                      <TableCell>{r.creative}</TableCell>
                      <TableCell>{r.text}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
          {publicationId ? (
            <PublicationProgress workspaceId={workspaceId} publicationId={publicationId} />
          ) : null}
        </CardContent>
      </Card>

      <AssetPicker
        workspaceId={workspaceId}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(a) => {
          if (!creatives.some((c) => c.id === a.id)) setCreatives([...creatives, a]);
          void creationApi.pushAsset(workspaceId, a.id, account).catch(() => undefined);
        }}
      />
      {editingAudience !== null && audiences[editingAudience] ? (
        <Dialog open onOpenChange={(o) => (o ? undefined : setEditingAudience(null))}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Segmentação de "{audiences[editingAudience].label}"</DialogTitle>
              <DialogDescription>
                Local, idade, gênero, interesses e públicos personalizados deste conjunto.
              </DialogDescription>
            </DialogHeader>
            <TargetingEditor
              workspaceId={workspaceId}
              account={account}
              value={audiences[editingAudience].targeting}
              onChange={(t) =>
                setAudiences(
                  audiences.map((x, k) => (k === editingAudience ? { ...x, targeting: t } : x)),
                )
              }
              optimizationGoal={goal}
            />
            <DialogFooter>
              <Button onClick={() => setEditingAudience(null)}>Pronto</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

// ---- Templates -----------------------------------------------------------------------------------------

function Templates({
  workspaceId,
  account,
  canWrite,
}: {
  workspaceId: string;
  account: string;
  canWrite: boolean;
}) {
  const [q, setQ] = useState("");
  const list = useQuery(queries.templates(workspaceId, q.trim()));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState<TemplateDto | null>(null);
  const use = useMutation({
    mutationFn: (t: TemplateDto) =>
      creationApi.useTemplate(workspaceId, t.id, { adAccountMetaId: account }),
    onSuccess: (d) => {
      void queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      toast.success(
        "Rascunho criado a partir do template. Complete página, pixel, públicos e mídias.",
      );
      void navigate({
        to: "/app/$workspaceId/assistente/$draftId",
        params: { workspaceId, draftId: d.id },
      });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => creationApi.deleteTemplate(workspaceId, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: creationKey(workspaceId) });
      setToDelete(null);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Templates do workspace</CardTitle>
        <CardDescription>
          Estruturas reaproveitáveis, sem ids da conta (página, pixel, públicos e mídias são
          escolhidos ao usar). Salve a partir de um rascunho (botão no assistente) ou de uma
          campanha existente (menu ⋯ na tabela de Campanhas).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input
          placeholder="Buscar template…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="sm:max-w-xs"
          aria-label="Buscar template"
        />
        {list.isPending ? (
          <LoadingState rows={2} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data.length === 0 ? (
          <EmptyState
            icon={<LayoutTemplate className="size-8" aria-hidden />}
            title="Nenhum template"
            description="Salve um rascunho ou uma campanha como template."
          />
        ) : (
          <ul className="divide-y">
            {list.data.map((t) => (
              <li key={t.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {objectiveLabel(t.objective)} · {t.adsetCount} conjunto(s) · {t.adCount}{" "}
                    anúncio(s) · {formatRelative(t.updatedAt)}
                    {t.createdByName ? ` · ${t.createdByName}` : ""}
                  </p>
                  {t.description ? (
                    <p className="text-sm text-muted-foreground">{t.description}</p>
                  ) : null}
                </div>
                {canWrite ? (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      onClick={() => use.mutate(t)}
                      loading={use.isPending}
                      disabled={!account}
                    >
                      Criar rascunho
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      aria-label="Excluir template"
                      onClick={() => setToDelete(t)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(o) => (o ? undefined : setToDelete(null))}
        title={`Excluir o template "${toDelete?.name ?? ""}"?`}
        confirmLabel="Excluir"
        destructive
        loading={remove.isPending}
        onConfirm={() => (toDelete ? remove.mutate(toDelete.id) : undefined)}
      />
    </Card>
  );
}

// ---- Publicações ------------------------------------------------------------------------------------

function Publications({ workspaceId, account }: { workspaceId: string; account: string }) {
  const list = useQuery(queries.publications(workspaceId, account || undefined));
  const [open, setOpen] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Publicações</CardTitle>
        <CardDescription>
          Histórico das publicações desta conta, com passos, erros e rollback.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {list.isPending ? (
          <LoadingState rows={2} />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data.length === 0 ? (
          <EmptyState
            title="Nenhuma publicação ainda"
            description="Publique um rascunho para ver o histórico aqui."
          />
        ) : (
          <ul className="divide-y">
            {list.data.map((p) => (
              <li key={p.id} className="space-y-2 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <PublicationBadge status={p.status} />
                  <span className="text-sm">
                    {p.kind === "massa"
                      ? "Criação em massa"
                      : p.kind === "duplicacao"
                        ? "Duplicação entre contas"
                        : "Assistente"}{" "}
                    · {p.createdIds.adsets.length} conjunto(s) · {p.createdIds.ads.length}{" "}
                    anúncio(s)
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatRelative(p.createdAt)}
                    {p.createdByName ? ` · ${p.createdByName}` : ""}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpen(open === p.id ? null : p.id)}
                  >
                    {open === p.id ? "Ocultar passos" : "Ver passos"}
                  </Button>
                </div>
                {p.error ? <p className="text-sm text-destructive">{p.error.message}</p> : null}
                {open === p.id ? (
                  <PublicationProgress workspaceId={workspaceId} publicationId={p.id} />
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export { formatMoney };
